import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import { DisbursementRequestModal } from '../../../cash-division/components/DisbursementRequestModal';
import type { DisbursementRequestListItem } from '../../../cash-division/disbursementRequestTypes';
import { myDisbursementRequestsApi, type CashRequester } from '../myDisbursementRequestsApi';
import { selfServiceSource } from '../selfServiceSource';

/**
 * Self Service › Cash Division › Requests — the signed-in employee's own requests to the Cash
 * Division: what they raised, and what Cash raised on their behalf.
 *
 * <b>The scope is the SERVER's</b>, fixed by the route, so nothing on this page can widen it.
 * <b>Nothing here moves a request along</b> — every step is the Cash Division's — so a row has
 * no actions beyond opening it.
 *
 * <b>There is no requesting-party column</b>: every row is the reader's own, so it would print
 * the same answer down the whole column — and naming them would need `personnel.read`, which
 * is exactly the key this surface exists to avoid handing out.
 */
export function MyDisbursementRequestsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [requester, setRequester] = useState<CashRequester | null>(null);

  // Seeded MET: until `/me` answers — and if it never does — Create stays enabled, because the
  // server is the authority and a failed lookup must not lock out an account that is linked.
  const [hasTransactionType, setHasTransactionType] = useState(true);
  const [hasPaymentType, setHasPaymentType] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    myDisbursementRequestsApi.me(controller.signal).then(setRequester).catch(() => undefined);

    myDisbursementRequestsApi
      .catalog('transaction-types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasTransactionType(rows.length > 0);
      })
      .catch(() => undefined);

    myDisbursementRequestsApi
      .catalog('payment-types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasPaymentType(rows.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      myDisbursementRequestsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          transactionType: query.facets.transactionType?.join(','),
          paymentType: query.facets.paymentType?.join(','),
        },
        signal,
      ),
    [],
  );

  const source = useMemo(() => selfServiceSource(requester), [requester]);

  return (
    <>
      <IndexPage<DisbursementRequestListItem>
        module="my-disbursement-requests"
        breadcrumb={['Self Service', 'Cash Division', 'Requests']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },
          { key: 'transactionType', label: 'Transaction type' },
          { key: 'paymentType', label: 'Payment type' },
        ]}
        // What a requester opens this with is how far their requests have got.
        folderNavFacet="stage"
        auditEntityTypes={['DisbursementRequest', 'DisbursementRequestItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        searchPlaceholder="Search by request number, particulars, voucher or payment reference"
        emptyMessage="You have no request to the Cash Division yet."
        /*
          A refusal on the ACCOUNT is announced, not only explained on hover — the server's own
          sentence, from the same resolve the create runs. The catalogs are named but link
          nowhere: an employee holds no key to the Cash Division's settings.
        */
        prerequisites={[
          {
            label: 'your employee record',
            met: requester?.error == null,
            detail: requester?.error ?? undefined,
            to: '/personnel',
            permission: 'personnel.update',
          },
          {
            label: 'transaction types',
            met: hasTransactionType,
            to: '/cash-division/settings/transaction-types',
            permission: 'cash-catalogs.read',
          },
          {
            label: 'payment types',
            met: hasPaymentType,
            to: '/cash-division/settings/payment-types',
            permission: 'cash-catalogs.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="my-disbursement-requests.create"
        onRowOpen={(row) => navigate(`/self-service/disbursement-requests/${row.id}`)}
        openPermission="my-disbursement-requests.read"
        otherInfo={(row) => row}
      />

      {creating ? (
        <DisbursementRequestModal
          open
          editing={null}
          self={source}
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            navigate(`/self-service/disbursement-requests/${saved.id}`);
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<DisbursementRequestListItem>[] = [
  {
    key: 'requestNumber',
    header: 'Request',
    width: 360,
    sortable: true,
    render: (row) => <TextCell code={row.requestNumber} value={row.particulars} clamp />,
  },
  {
    key: 'transactionType',
    header: 'Transaction type',
    width: 180,
    summary: true,
    render: (row) => <TextCell value={row.transactionTypeName} />,
  },
  {
    key: 'paymentType',
    header: 'Payment type',
    width: 150,
    render: (row) => <TextCell value={row.paymentTypeName} />,
  },
  {
    key: 'amount',
    header: 'Amount',
    width: 140,
    align: 'right',
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={formatMoney(row.amount)} mono numeric />,
  },
  {
    key: 'voucherNumber',
    header: 'Voucher no.',
    width: 150,
    render: (row) => <TextCell value={row.voucherNumber} mono />,
  },
  {
    key: 'stage',
    header: 'Stage',
    width: 160,
    sortable: true,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
